import React from 'react';
import { Link } from 'react-router-dom';
import {
    Building,
    Shield,
    GraduationCap,
    Briefcase,
    Heart,
    ArrowRight,
    CheckCircle2,
    Users
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernWhoWeServe.scss';

const ModernWhoWeServe = () => {
    const sectors = [
        {
            icon: <Building size={36} />,
            title: 'Federal Civilian',
            description: 'Empowering Federal Civilian Agencies Through Digital Innovation',
            details: 'We help federal agencies modernize their IT infrastructure, streamline operations, and deliver better citizen services.',
            features: ['IT Modernization', 'Cloud Migration', 'Security Compliance', 'Data Analytics'],
            link: '/federal-civilian',
            color: 'primary'
        },
        {
            icon: <Shield size={36} />,
            title: 'Department of Defense',
            description: 'Empower the Warfighter | Execute Innovation | Enable Readiness',
            details: 'Supporting mission-critical operations with secure, reliable, and innovative technology solutions.',
            features: ['Secure Communications', 'Mission Systems', 'Cyber Defense', 'Logistics Support'],
            link: '/department',
            color: 'blue'
        },
        {
            icon: <GraduationCap size={36} />,
            title: 'SLED',
            description: 'Simplify Complex Technology. Focus on Your Mission.',
            details: 'Supporting State & Local Governments, K-12, and Higher Education institutions with tailored solutions.',
            features: ['Education Technology', 'Smart City Solutions', 'Public Safety', 'Administrative Systems'],
            link: '/sled',
            color: 'purple'
        },
        {
            icon: <Briefcase size={36} />,
            title: 'Commercial',
            description: 'Ushering in the Next Generation of Technology Solutions',
            details: 'Driving measurable outcomes for businesses with innovative technology and strategic partnerships.',
            features: ['Enterprise Solutions', 'Digital Transformation', 'Cloud Services', 'Managed IT'],
            link: '/commercial',
            color: 'orange'
        },
        {
            icon: <Heart size={36} />,
            title: 'Health Care',
            description: 'Streamline Care with a Unified Digital Health Platform',
            details: 'Delivering healthcare technology solutions that improve patient outcomes and operational efficiency.',
            features: ['EHR Integration', 'Telehealth', 'Medical Devices', 'HIPAA Compliance'],
            link: '/health-care',
            color: 'red'
        }
    ];

    const whyChoose = [
        'Deep sector-specific expertise and understanding',
        'Compliance-ready solutions for regulated industries',
        'Proven track record with government agencies',
        'Strong OEM partnerships for best-in-class products',
        'Dedicated account managers for each sector',
        'Rapid procurement and deployment capabilities'
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Markets Served"
                subtitle="Jaak Tech delivers specialized solutions across diverse sectors, each with unique challenges and requirements"
                breadcrumbs={[{ label: 'Markets Served' }]}
            />

            {/* Sectors Grid */}
            <section className="jk-sectors">
                <div className="jk-sectors__container">
                    <div className="jk-sectors__header">
                        <span className="jk-section-label">
                            <Users size={16} />
                            Industries We Serve
                        </span>
                        <h2 className="jk-sectors__title">
                            Tailored Solutions for
                            <span className="highlight"> Every Sector</span>
                        </h2>
                        <p className="jk-sectors__subtitle">
                            We understand that each industry has unique challenges. Our specialized teams
                            deliver customized solutions that address your specific needs.
                        </p>
                    </div>

                    <div className="jk-sectors__grid">
                        {sectors.map((sector, index) => (
                            <Link
                                key={index}
                                to={sector.link}
                                className={`jk-sectors__card jk-sectors__card--${sector.color}`}
                            >
                                <div className="jk-sectors__card-icon">
                                    {sector.icon}
                                </div>
                                <h3 className="jk-sectors__card-title">{sector.title}</h3>
                                <p className="jk-sectors__card-tagline">{sector.description}</p>
                                <p className="jk-sectors__card-description">{sector.details}</p>

                                <ul className="jk-sectors__card-features">
                                    {sector.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <span className="jk-sectors__card-link">
                                    Learn More
                                    <ArrowRight size={18} />
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Section */}
            <section className="jk-sectors-why">
                <div className="jk-sectors-why__container">
                    <div className="jk-sectors-why__grid">
                        <div className="jk-sectors-why__content">
                            <span className="jk-section-label">
                                <CheckCircle2 size={16} />
                                Our Expertise
                            </span>
                            <h2 className="jk-sectors-why__title">
                                Why Organizations
                                <span className="highlight"> Trust Us</span>
                            </h2>
                            <p className="jk-sectors-why__text">
                                With years of experience serving diverse sectors, we've built deep
                                expertise in understanding the unique requirements, compliance needs,
                                and operational challenges of each industry we serve.
                            </p>

                            <ul className="jk-sectors-why__list">
                                {whyChoose.map((item, index) => (
                                    <li key={index} className="jk-sectors-why__item">
                                        <CheckCircle2 size={20} className="jk-sectors-why__item-icon" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>

                            <Link to="/contact" className="jk-btn jk-btn--primary">
                                Contact Our Team
                                <ArrowRight size={20} />
                            </Link>
                        </div>

                        <div className="jk-sectors-why__stats">
                            <div className="jk-sectors-why__stat-card">
                                <span className="jk-sectors-why__stat-number">500+</span>
                                <span className="jk-sectors-why__stat-label">Government Clients</span>
                            </div>
                            <div className="jk-sectors-why__stat-card">
                                <span className="jk-sectors-why__stat-number">50+</span>
                                <span className="jk-sectors-why__stat-label">OEM Partners</span>
                            </div>
                            <div className="jk-sectors-why__stat-card">
                                <span className="jk-sectors-why__stat-number">15+</span>
                                <span className="jk-sectors-why__stat-label">Years Experience</span>
                            </div>
                            <div className="jk-sectors-why__stat-card">
                                <span className="jk-sectors-why__stat-number">99%</span>
                                <span className="jk-sectors-why__stat-label">Client Satisfaction</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernWhoWeServe;
